import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { TrendingUp } from 'lucide-react';
import ReactApexChart from 'react-apexcharts';
import useOSINTCounts from '../../hooks/useOSINTCounts';
import { OSINTCount } from '../../api/countService';
import { useTranslation } from 'react-i18next';
import LoaderWhite from '../LoaderWhite';

type MetricType = 'data_breaches' | 'vulnerabilities' | 'phishing_attempts';

const metricOptions: MetricType[] = [
  'data_breaches',
  'vulnerabilities',
  'phishing_attempts',
];

const dateRangeOptions = ['1month', '6months', '1year'];

const colorMap: Record<MetricType, string> = {
  data_breaches: '#00BFFF',
  vulnerabilities: '#FF6B6B',
  phishing_attempts: '#9F7AEA',
};

const ChartsSection: React.FC = () => {
  const { t } = useTranslation();
  const [metricType, setMetricType] = useState<MetricType>('data_breaches');
  const [dateRange, setDateRange] = useState('1month');

  const [series, setSeries] = useState<any[]>([]);
  const [options, setOptions] = useState<any>({});

  const { counts, loading, error } = useOSINTCounts();

  useEffect(() => {
    if (!counts) return;

    const rawData: OSINTCount[] = counts[metricType] || [];

    const now = new Date();
    const startDate = new Date();

    if (dateRange === '1month') startDate.setMonth(now.getMonth() - 1);
    else if (dateRange === '6months') startDate.setMonth(now.getMonth() - 6);
    else if (dateRange === '1year') startDate.setFullYear(now.getFullYear() - 1);

    const filteredData = rawData
      .filter((item: OSINTCount) => new Date(item.date).getTime() >= startDate.getTime())
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    const dataPoints = filteredData.map((item: OSINTCount) => ({
      x: new Date(item.date).getTime(),
      y: item.count,
    }));

    setSeries([
      {
        name: t(`charts.metrics.${metricType}`),
        data: dataPoints,
      },
    ]);

    setOptions({
      chart: {
        type: 'line',
        height: 350,
        zoom: { enabled: false },
        toolbar: { show: false },
        background: '#1a202c',
        foreColor: '#fff',
      },
      dataLabels: { enabled: false },
      stroke: { curve: 'smooth', width: 3 },
      grid: {
        borderColor: '#4a5568',
        row: {
          colors: ['transparent'],
        },
      },
      xaxis: {
        type: 'datetime',
        labels: {
          style: { colors: '#e2e8f0' },
          format: 'dd/MM/yyyy',
        },
        axisBorder: { color: '#4a5568' },
        axisTicks: { color: '#4a5568' },
      },
      yaxis: {
        labels: {
          style: { colors: '#e2e8f0' },
        },
      },
      tooltip: {
        theme: 'dark',
        x: { format: 'dd/MM/yyyy' },
      },
      colors: [colorMap[metricType]],
    });
  }, [counts, metricType, dateRange, t]);

  if (loading) return <LoaderWhite />;
  if (error) return <p className="text-center text-red-500">{error}</p>;
  if (!counts) return <p className="text-center text-gray-500">No hay datos disponibles.</p>;

  return (
    <div className="grid grid-cols-1 gap-6">
      <Card className="bg-[#0f172a] border border-gray-800 shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center justify-center gap-2 text-3xl font-extrabold text-white">
            <TrendingUp className="h-8 w-8 text-white" />
            {t(`charts.metrics.${metricType}`)}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 mb-6 justify-center">
            <select
              value={metricType}
              onChange={(e) => setMetricType(e.target.value as MetricType)}
              className="p-2 border rounded bg-gray-800 text-white"
            >
              {metricOptions.map((option) => (
                <option key={option} value={option}>
                  {t(`charts.metrics.${option}`)}
                </option>
              ))}
            </select>

            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="p-2 border rounded bg-gray-800 text-white"
            >
              {dateRangeOptions.map((range) => (
                <option key={range} value={range}>
                  {t(`charts.ranges.${range}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="h-[350px] bg-[#1a202c] rounded-lg p-2">
            {series.length > 0 ? (
              <ReactApexChart options={options} series={series} type="line" height={350} />
            ) : (
              <p className="text-center p-4 text-white">No hay datos disponibles en este rango.</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ChartsSection;
